import React from "react";
import { vehicleBanner, vehicleBannerPromo } from "../content/landingContent";
import { CtaButton } from "./CtaButton";
import { Reveal } from "./Reveal";
import { useResourceImage } from "../lib/useResourceImage";
import { useContentField, useContentList, useContentBoolean, useLinks } from "../lib/useContent";
import { trackLead, trackViewContent } from "../lib/metaPixel";
import bannerLeft from "../assets/vehicles/banner-left.jpg";
import bannerRight from "../assets/vehicles/banner-right.jpg";
import promoMan from "../assets/vehicles/promo-man.jpg";
import promoWoman from "../assets/vehicles/promo-woman.jpg";
import promoSecondary from "../assets/vehicles/promo-secondary.jpg";
import { ArrowRight, Search } from "lucide-react";

export const VehicleBanner: React.FC = () => {
  const { src: bannerLeftSrc } = useResourceImage("banner.vehicleLeft", bannerLeft);
  const { src: bannerRightSrc } = useResourceImage("banner.vehicleRight", bannerRight);
  const { src: promoManSrc } = useResourceImage("promo.man", promoMan);
  const { src: promoWomanSrc } = useResourceImage("promo.woman", promoWoman);
  const { src: promoSecondarySrc } = useResourceImage("promo.secondary", promoSecondary);
  const links = useLinks();

  const promoEnabled = useContentBoolean("vehicleBannerPromo.enabled", vehicleBannerPromo.enabled);

  // Promoción de lanzamiento usa sus propias imágenes (modelo hombre/mujer +
  // imagen secundaria), totalmente independientes de las 2 imágenes del
  // Banner automotriz y de las del carrusel de vehículos destacados.
  const leftSrc = promoEnabled ? promoManSrc : bannerLeftSrc;
  const rightSrc = promoEnabled ? promoWomanSrc : bannerRightSrc;

  const eyebrow = useContentField("vehicleBanner.eyebrow", vehicleBanner.eyebrow);
  const title = useContentField("vehicleBanner.title", vehicleBanner.title);
  const text = useContentField("vehicleBanner.text", vehicleBanner.text);
  const benefits = useContentList("vehicleBanner.benefitsList", vehicleBanner.benefits);
  const ctaExplore = useContentField("vehicleBanner.ctaExplore", vehicleBanner.ctaExplore);
  const ctaExploreUrlOverride = useContentField("vehicleBanner.ctaExploreUrl", vehicleBanner.ctaExploreUrl);
  const ctaPublish = useContentField("vehicleBanner.ctaPublish", vehicleBanner.ctaPublish);
  const ctaPublishUrlOverride = useContentField("vehicleBanner.ctaPublishUrl", vehicleBanner.ctaPublishUrl);
  const imageAlt1 = useContentField("vehicleBanner.imageAlt1", vehicleBanner.imageAlt1);
  const imageAlt2 = useContentField("vehicleBanner.imageAlt2", vehicleBanner.imageAlt2);

  const promoEyebrow = useContentField("vehicleBannerPromo.eyebrow", vehicleBannerPromo.eyebrow);
  const promoTitle = useContentField("vehicleBannerPromo.title", vehicleBannerPromo.title);
  const promoText = useContentField("vehicleBannerPromo.text", vehicleBannerPromo.text);
  const promoBenefits = useContentList("vehicleBannerPromo.benefitsList", vehicleBannerPromo.benefits);
  const promoCtaPublish = useContentField("vehicleBannerPromo.ctaPublish", vehicleBannerPromo.ctaPublish);
  const promoCtaExplore = useContentField("vehicleBannerPromo.ctaExplore", vehicleBannerPromo.ctaExplore);

  const displayEyebrow = promoEnabled ? promoEyebrow : eyebrow;
  const displayTitle = promoEnabled ? promoTitle : title;
  const displayText = promoEnabled ? promoText : text;
  const displayBenefits = promoEnabled ? promoBenefits : benefits;
  const exploreHref = ctaExploreUrlOverride || links.exploreUrl;
  const publishHref = ctaPublishUrlOverride || links.appUrl;

  return (
    <section
      data-testid="vehicle-banner-section"
      className="relative bg-motor-mist py-20 sm:py-24 lg:py-28 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div className="order-2 lg:order-1">
          <Reveal>
            <span
              data-testid="vehicle-banner-eyebrow"
              className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-motor-red"
            >
              {displayEyebrow}
            </span>
            <h2
              data-testid="vehicle-banner-title"
              className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-motor-ink leading-tight"
            >
              {displayTitle}
            </h2>
            <p
              data-testid="vehicle-banner-text"
              className="mt-5 text-base text-neutral-600 max-w-md leading-relaxed"
            >
              {displayText}
            </p>
            {displayBenefits.length > 0 && (
              <ul data-testid="vehicle-banner-benefits" className="mt-5 space-y-2 max-w-md">
                {displayBenefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-sm text-neutral-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-motor-red shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
              <CtaButton
                href={promoEnabled ? links.appUrl : exploreHref}
                onClick={() =>
                  promoEnabled
                    ? trackLead("vehicle_banner_download")
                    : trackViewContent("vehicle_banner_explore")
                }
                testId="vehicle-banner-explore-button"
                icon={promoEnabled ? <ArrowRight className="h-4 w-4" /> : <Search className="h-4 w-4" />}
              >
                {promoEnabled ? promoCtaPublish : ctaExplore}
              </CtaButton>
              <CtaButton
                href={promoEnabled ? links.exploreUrl : publishHref}
                onClick={() =>
                  promoEnabled
                    ? trackViewContent("vehicle_banner_explore")
                    : trackLead("vehicle_banner_download")
                }
                variant="outline"
                testId="vehicle-banner-publish-button"
                icon={promoEnabled ? <Search className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              >
                {promoEnabled ? promoCtaExplore : ctaPublish}
              </CtaButton>
            </div>
          </Reveal>
        </div>

        <div className="order-1 lg:order-2">
          {/* Mobile / tablet vertical: stacked, no overlap, no cropping */}
          <div className="relative flex flex-col gap-4 sm:gap-5 lg:hidden">
            {promoEnabled && (
              <img
                src={promoSecondarySrc}
                alt="Promoción de lanzamiento"
                data-testid="promo-secondary-badge-mobile"
                className="absolute -top-3 -right-3 z-30 h-16 w-16 rounded-full border-4 border-white shadow-lg object-cover"
              />
            )}
            <div className="overflow-hidden rounded-2xl border-4 border-white shadow-xl bg-white">
              <img
                src={leftSrc}
                alt={imageAlt1}
                className="block w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="overflow-hidden rounded-2xl border-4 border-white shadow-xl bg-white">
              <img
                src={rightSrc}
                alt={imageAlt2}
                className="block w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
          </div>

          {/* Desktop / tablet horizontal: overlapping cards, near-equal size */}
          <div className="hidden lg:block relative w-full max-w-md mx-auto">
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-motor-red/15 blur-3xl rounded-full scale-90"
            />
            {promoEnabled && (
              <img
                src={promoSecondarySrc}
                alt="Promoción de lanzamiento"
                data-testid="promo-secondary-badge-desktop"
                className="absolute -top-4 -right-4 z-30 h-20 w-20 rounded-full border-4 border-white shadow-lg object-cover"
              />
            )}
            <Reveal variant="right" delay={0.1} className="relative z-10 w-full">
              <div className="overflow-hidden rounded-2xl border-4 border-white shadow-2xl bg-white">
                <img
                  src={leftSrc}
                  alt={imageAlt1}
                  className="block w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal variant="left" delay={0.25} className="absolute z-20 left-[8%] top-[26%] w-[92%]">
              <div className="overflow-hidden rounded-2xl border-4 border-white shadow-2xl bg-white">
                <img
                  src={rightSrc}
                  alt={imageAlt2}
                  className="block w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
